// @ts-nocheck

import { isBetEligible } from '@winamax/missions-lib';
import React from 'react';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import { getDispatchFromProps, getItemIdOrOddIdFromProps } from '../../../../selectors/_propsGetters_';
import {
  selectBasketIsBoomActive,
  selectBasketIsCashout,
  selectBasketItems,
  selectBasketMode,
  selectBasketState,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketShowResume, makeBasketItemsMatchesSelector } from '../../../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems } from '../../../../selectors/basketItems.selector';
import { selectEntitiesState } from '../../../../selectors/entities/entitiesPrimary.selector';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { BasketModes, BasketStatuses } from '../../../../types/basket';
import { isBasketItemsStartWithinEndDate } from '../../../../utils/operations';
import { basketItemSetBoomActive, basketSetBoomActive } from '../../actions/basket';
import { BoomAssetsBySkinId } from '../../constants/assets';
import { BoomLabelsBySkinId } from '../../constants/labels';
import { selectBasketBoomState } from '../../selectors/basket.selector';
import {
  selectBoomGlobalOperationEndDate,
  selectBoomGlobalSkinId,
} from '../../selectors/global/globalPrimary.selector';
import { selectIsBoomPlayable } from '../../selectors/runtime.selector';
import {
  selectBoomCurrentStepEligibility,
  selectBoomCurrentStepId,
  selectBoomCurrentStepStepId,
} from '../../selectors/steps.selector';
import { selectBoomUserProgress, selectBoomUserStatus } from '../../selectors/user/userPrimary.selector';
import { BoomStepStatuses } from '../../types/steps';
import { BoomUserStatuses } from '../../types/user';

const SwitchIconImg = styled.img`
  object-fit: cover;
  background-repeat: no-repeat;
  height: 1.6em;
  margin-right: 0.5em;
`;

const makeBoomBasketSwitchPropsSelector = () => {
  const selectBasketItemsMatches = makeBasketItemsMatchesSelector();

  const selectIsCurrentSelectionEligible = createSelector(
    [
      selectBasketMode,
      selectBasketItems,
      getItemIdOrOddIdFromProps,
      selectBoomCurrentStepEligibility,
      selectBasketState,
      selectEntitiesState,
      selectBoomUserProgress,
      selectBoomCurrentStepStepId,
    ],
    (basketMode, basketItems, oddsId, eligibility, basket, entities, userProgress, currentStepId): boolean => {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;
      const item = basketItems[oddsId];

      if (userProgress[currentStepId]?.status !== BoomStepStatuses.OPEN) {
        return false;
      }

      if (item) {
        return isBetEligible({
          eligibility,
          basket,
          item,
          entities,
        });
      } else {
        if (isOneSimpleBet) {
          const foundOddsId = Object.keys(basketItems)[0];

          if (foundOddsId) {
            const foundItem = basketItems[foundOddsId];

            if (foundItem) {
              return isBetEligible({
                eligibility,
                basket,
                item: foundItem,
                entities,
              });
            }
          }
        } else {
          return isBetEligible({
            eligibility,
            basket,
            entities,
          });
        }

        return false;
      }
    },
  );

  const selectIsDisabled = createSelector(
    [selectBasketStatus, selectIsCurrentSelectionEligible],
    (basketStatus, isCurrentSelectionEligible) =>
      basketStatus === BasketStatuses.PENDING || !isCurrentSelectionEligible,
  );

  const selectIsToggled = createSelector(
    [selectBasketMode, getItemIdOrOddIdFromProps, selectBasketItems, selectBasketIsBoomActive],
    (basketMode, oddsId, basketItems, isBasketBoomActive) => {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isModeCombined = basketMode === BasketModes.COMBINED;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

      if (isOneSimpleBet) {
        const foundOddsId = Object.keys(basketItems)[0];

        return !!basketItems[foundOddsId]?.isBoomActive;
      }
      if (isModeSimple) {
        return !!basketItems[oddsId]?.isBoomActive;
      }
      if (isModeCombined) {
        return !!isBasketBoomActive;
      }
    },
  );

  const selectIsAutoToggleOnRender = createSelector(
    [
      selectBasketMode,
      getItemIdOrOddIdFromProps,
      selectBasketItems,
      selectBasketItemsMatches,
      selectBoomGlobalOperationEndDate,
    ],
    (basketMode, oddsId, basketItems, basketItemsMatches, boomEndDate) => {
      const item = basketItems[oddsId];

      return isBasketItemsStartWithinEndDate(basketMode, basketItemsMatches, boomEndDate, item);
    },
  );

  const selectLabel = createSelector(
    [selectBoomGlobalSkinId, selectBoomCurrentStepId],
    (skinId, currentStepId) =>
      `${BoomLabelsBySkinId[skinId]?.OperationTitle} - ${
        BoomLabelsBySkinId[skinId]?.StepShortTitleByStepId[currentStepId] ?? ''
      }`,
  );

  // eslint-disable-next-line react/display-name
  const selectPrimaryIcon = createSelector([selectBoomGlobalSkinId], (skinId) => (props) => (
    <SwitchIconImg {...props} src={BoomAssetsBySkinId[skinId].BetslipIcon} />
  ));

  const selectOnToggle = createSelector(
    [
      getDispatchFromProps,
      getItemIdOrOddIdFromProps,
      selectIsCurrentSelectionEligible,
      selectBasketMode,
      selectBasketItems,
      selectBoomGlobalSkinId,
      selectBoomCurrentStepId,
    ],
    (dispatch, oddsId, isCurrentSelectionEligible, basketMode, basketItems, skinId, stepId) => (isSelected) => {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

      if (oddsId) {
        dispatch(
          basketItemSetBoomActive({
            oddsId,
            isActive: isSelected ? isCurrentSelectionEligible ?? false : false,
            skinId,
            stepId,
          }),
        );
      } else {
        if (isOneSimpleBet) {
          const foundOddsId = Object.keys(basketItems)[0];

          if (foundOddsId) {
            dispatch(
              basketItemSetBoomActive({
                oddsId: foundOddsId,
                isActive: isSelected ? isCurrentSelectionEligible ?? false : false,
                skinId,
                stepId,
              }),
            );
          }
        } else {
          dispatch(
            basketSetBoomActive({
              isActive: isSelected ? isCurrentSelectionEligible ?? false : false,
              skinId,
              stepId,
            }),
          );
        }
      }
    },
  );

  const selectIsBoomBasketVisible = createSelector(
    [
      selectBoomUserStatus,
      selectBasketShowResume,
      getItemIdOrOddIdFromProps,
      selectBasketMode,
      selectBasketEnhancedItems,
      selectUserConnected,
      selectBasketIsCashout,
      selectBasketBoomState,
      selectIsBoomPlayable,
    ],
    (userStatus, showResume, oddsId, basketMode, items, userIsConnected, isCashout, basketBoom, isPlayable) => {
      if (isPlayable && userIsConnected && userStatus === BoomUserStatuses.IN && !showResume) {
        const isModeSimple = basketMode === BasketModes.SIMPLE;
        const isModeSystem = basketMode === BasketModes.SYSTEM;

        const isOneSimpleBet = isModeSimple && items.length === 1;
        const isMultipleSimpleBets = isModeSimple && items.length > 1;

        return (
          !isCashout &&
          !isModeSystem &&
          ((isOneSimpleBet && !oddsId) || (!isModeSimple && !oddsId) || (isMultipleSimpleBets && oddsId)) &&
          !!basketBoom
        );
      }

      return false;
    },
  );

  return createSelector(
    [
      selectIsBoomBasketVisible,
      selectIsDisabled,
      selectIsToggled,
      selectIsAutoToggleOnRender,
      selectLabel,
      selectOnToggle,
      selectPrimaryIcon,
      selectBasketMode,
    ],
    (isVisible, disabled, toggled, autoToggleOnRender, label, onToggle, PrimaryIcon, basketMode) => ({
      isVisible,
      disabled,
      toggled,
      autoToggleOnRender,
      label,
      onToggle,
      PrimaryIcon,
      basketMode,
    }),
  );
};

export default makeBoomBasketSwitchPropsSelector;
